import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { authClient, useSession } from '@/lib/auth-client';
import { AuthLayout } from '@/components/AuthLayout';
import { ButtonLink } from '@/components/ui';

/**
 * /accept-invite/:id — landing page for invite emails.
 *
 * Flow:
 *   1. If not signed in → redirect to /signup?invitation=<id> so they can
 *      create an account with the same email (or sign in if they already
 *      have one).
 *   2. If signed in → call Better-Auth's acceptInvitation, redirect to /app.
 *   3. Any error → show it inline with a "back home" link.
 */
export default function AcceptInvite() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: session, isPending } = useSession();

  const [status, setStatus] = useState<'pending' | 'accepting' | 'done' | 'error'>('pending');
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (isPending) return;
    if (!session) {
      // Send them to sign up — they'll come back to /accept-invite/:id after.
      navigate(`/signup?invitation=${id}`, { replace: true });
      return;
    }
    if (status !== 'pending') return;
    setStatus('accepting');
    authClient.organization
      .acceptInvitation({ invitationId: id })
      .then(({ error }) => {
        if (error) {
          setStatus('error');
          setErr(error.message || 'Could not accept invitation.');
          return;
        }
        setStatus('done');
        setTimeout(() => navigate('/app'), 1200);
      })
      .catch((e) => {
        setStatus('error');
        setErr((e as Error).message);
      });
  }, [id, isPending, session, navigate, status]);

  const isBusy = status === 'pending' || status === 'accepting' || isPending;

  return (
    <AuthLayout
      title={
        isBusy ? 'Joining workspace…' : status === 'done' ? "You're in" : "Couldn't accept invitation"
      }
      subtitle={
        isBusy ? "We're processing your invitation. One sec." : undefined
      }
    >
      {status === 'done' && (
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <div
            className="flex h-16 w-16 items-center justify-center rounded-full text-2xl"
            style={{
              background: 'rgba(125, 255, 162, 0.15)',
              color: '#7dffa2',
              boxShadow: '0 0 32px -8px rgba(125, 255, 162, 0.5)',
            }}
          >
            ✓
          </div>
          <p className="text-sm text-[var(--color-on-surface-variant)]">
            Redirecting to your workspace…
          </p>
        </div>
      )}

      {isBusy && (
        <div className="flex flex-col items-center gap-4 py-6">
          <div
            className="h-12 w-12 animate-spin rounded-full border-2 border-[var(--color-border)] border-t-[var(--color-primary)]"
            aria-hidden
          />
        </div>
      )}

      {status === 'error' && (
        <div className="space-y-4">
          <p className="text-sm text-[var(--color-destructive)]">{err}</p>
          <p className="text-sm text-[var(--color-on-surface-variant)]">
            The invitation may have expired or been cancelled. Ask the workspace
            owner to send you a new one.
          </p>
          <ButtonLink to="/" variant="ghost" fullWidth>
            Go home
          </ButtonLink>
        </div>
      )}
    </AuthLayout>
  );
}
